import React from "react";
import { Button, message } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

const InvestmentStatementGenerator = ({ investment }) => {
  const generateStatement = () => {
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Add Watermark
      doc.setFontSize(40);
      doc.setGState(new doc.GState({ opacity: 0.07 }));
      doc.text("CONFIDENTIAL", 60, 140, { angle: 45 });
      doc.setGState(new doc.GState({ opacity: 1 }));

      // Header Section
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${moment().format("DD MMM YYYY HH:mm")}`, 15, 15);
      doc.text(`ISIN: ${investment.isin || "N/A"}`, 15, 20);

      // Company Logo and Info
      doc.setFontSize(16);
      doc.setTextColor(40);
      doc.setFont("helvetica", "bold");
      const investorRecord = investment.investor || investment.investor_id || {};
      doc.text(`${investorRecord.company || "Company"}`, 105, 25, { align: "center" });
      doc.setFontSize(12);
      doc.text("Investment Portfolio Statement", 105, 32, { align: "center" });

      // Investor Information
      const investorName = investorRecord.name || `${investorRecord.first_name || ""} ${investorRecord.last_name || ""}`.trim();
      const investorInfo = [
        ["Investor Name", investorName || "N/A"],
        ["Account Number", investment.account_number || "N/A"],
        ["Tax ID", investorRecord.tax_id || "N/A"],
        [
          "Investment Date",
          moment(investment.investment_date).format("DD MMM YYYY"),
        ],
        [
          "Maturity Date",
          moment(investment.maturity_date).format("DD MMM YYYY"),
        ],
      ];

      doc.autoTable({
        startY: 45,
        body: investorInfo,
        theme: "grid",
        styles: { font: "helvetica", fontSize: 10 },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 50 },
          1: { cellWidth: 50 },
        },
      });

      // Key Metrics
      const metricsData = [
        [
          "Principal Amount",
          `${investment.principal_amount?.toLocaleString()} ZMW`,
        ],
        [
          "Accrued Interest",
          `${investment.expected_interest?.toLocaleString()} ZMW`,
        ],
        [
          "Current Value",
          `${investment.total_maturity_amount?.toLocaleString()} ZMW`,
        ],
        ["Annualized Yield", `${investment.interest_rate}%`],
        ["Risk Rating", "AA- (Low Risk)"],
      ];

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 10,
        body: metricsData,
        theme: "grid",
        styles: { font: "helvetica", fontSize: 10 },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 50 },
          1: { cellWidth: 50 },
        },
      });

      // Transaction History
      const transactions =
        investment.transactions?.map((t) => [
          moment(t.transaction_date).format("DD MMM YYYY"),
          t.transaction_type.toUpperCase(),
          `${t.amount?.toLocaleString()} ZMW`,
          t.reference_number || "N/A",
        ]) || [];

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 15,
        head: [["Date", "Type", "Amount", "Reference"]],
        body: transactions,
        theme: "grid",
        styles: { font: "helvetica", fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 25 },
          2: { cellWidth: 30 },
          3: { cellWidth: 40 },
        },
      });

      // Legal Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
          "This document contains confidential information intended solely for the recipient. Any unauthorized use is prohibited.",
          15,
          280
        );
        doc.text(`Page ${i} of ${pageCount}`, 190, 280, { align: "right" });
      }

      doc.save(`Investment-Statement-${investment.id}.pdf`);
    } catch (error) {
      message.error(`Statement generation failed: ${error.message}`);
    }
  };

  if (!investment) return null;

  return (
    <Button
      type="default"
      icon={<FilePdfOutlined />}
      onClick={generateStatement}
      style={{ width: "100%", marginTop: 8 }}
    >
      Generate Official Statement
    </Button>
  );
};

export default InvestmentStatementGenerator;
